import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ChatInputRequestOption {
  id: string;
  label: string;
}

export interface ChatInputRequest {
  id: string;
  question: string;
  options: ChatInputRequestOption[];
}

export type ChatInputRequestAnswer =
  | { kind: "choice"; optionId: string; details?: string }
  | { kind: "other"; text: string }
  | { kind: "skip" };

interface ChatInputRequestDockBaseProps {
  request: ChatInputRequest;
  onSubmit: (answer: ChatInputRequestAnswer) => void;
  isDisabled?: boolean;
  className?: string;
}

interface ChatInputRequestDockWithDetailsProps
  extends ChatInputRequestDockBaseProps {
  details: string;
  onDetailsChange: (details: string) => void;
  showDetails?: true;
}

/** Hides the details field and its typed "Other…" answer. */
interface ChatInputRequestDockWithoutDetailsProps
  extends ChatInputRequestDockBaseProps {
  details?: never;
  onDetailsChange?: never;
  showDetails: false;
}

export type ChatInputRequestDockProps =
  | ChatInputRequestDockWithDetailsProps
  | ChatInputRequestDockWithoutDetailsProps;

const shortcutForIndex = (index: number): string =>
  String.fromCharCode("A".charCodeAt(0) + index);

const ShortcutBadge = ({
  children,
  isSelected,
}: {
  children: string;
  isSelected: boolean;
}) => (
  <span
    aria-hidden
    className={cn(
      "flex size-6 shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600",
      isSelected && "border-violet-700 bg-violet-700 text-white",
    )}
  >
    {children}
  </span>
);

export const ChatInputRequestDock = ({
  request,
  details = "",
  onDetailsChange,
  onSubmit,
  showDetails = true,
  isDisabled = false,
  className,
}: ChatInputRequestDockProps) => {
  const questionId = useId();
  const dockRef = useRef<HTMLFieldSetElement | null>(null);
  const detailsInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const typedAnswer = details.trim();
  const isOtherActive =
    showDetails &&
    selectedOptionId == null &&
    (isOtherSelected || typedAnswer.length > 0);
  const canSubmit =
    !isDisabled &&
    (selectedOptionId != null || (showDetails && typedAnswer.length > 0));
  const hasAnswer = selectedOptionId != null || isOtherActive;

  useEffect(() => {
    setSelectedOptionId(null);
    setIsOtherSelected(false);
    dockRef.current?.focus();
  }, [request.id]);

  const submit = () => {
    if (!canSubmit) return;
    if (selectedOptionId != null) {
      onSubmit({
        kind: "choice",
        optionId: selectedOptionId,
        ...(showDetails && { details }),
      });
      return;
    }
    onSubmit({ kind: "other", text: typedAnswer });
  };

  const handleEscape = () => {
    if (hasAnswer) {
      dockRef.current?.focus();
      return;
    }
    onSubmit({ kind: "skip" });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFieldSetElement>) => {
    if (isDisabled || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === "Escape") {
      event.preventDefault();
      handleEscape();
      return;
    }

    const shortcutIndex =
      event.key.length === 1
        ? event.key.toUpperCase().charCodeAt(0) - "A".charCodeAt(0)
        : -1;
    const selectedOption = request.options[shortcutIndex];
    if (selectedOption) {
      event.preventDefault();
      setSelectedOptionId(selectedOption.id);
      setIsOtherSelected(false);
    } else if (showDetails && shortcutIndex === request.options.length) {
      event.preventDefault();
      setSelectedOptionId(null);
      setIsOtherSelected(true);
      detailsInputRef.current?.focus();
    } else if (event.key === "Enter" && canSubmit) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg",
        className,
      )}
    >
      <fieldset
        ref={dockRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        aria-labelledby={questionId}
        className="px-4 pb-3 pt-3 focus:outline-none"
        disabled={isDisabled}
      >
        <p
          id={questionId}
          className="text-sm font-semibold leading-5 text-gray-800"
        >
          {request.question}
        </p>
        <div className="mt-2 space-y-0.5">
          {request.options.map((option, index) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 has-disabled:cursor-not-allowed has-disabled:opacity-50 has-focus-visible:ring-2 has-focus-visible:ring-violet-700"
            >
              <input
                type="radio"
                name={`input-request-${request.id}`}
                value={option.id}
                checked={selectedOptionId === option.id}
                onChange={() => {
                  setSelectedOptionId(option.id);
                  setIsOtherSelected(false);
                }}
                className="sr-only"
              />
              <ShortcutBadge isSelected={selectedOptionId === option.id}>
                {shortcutForIndex(index)}
              </ShortcutBadge>
              <span className="pt-0.5 leading-5">{option.label}</span>
            </label>
          ))}
          {showDetails && (
            <label className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50 has-disabled:cursor-not-allowed has-disabled:opacity-50 has-focus-visible:ring-2 has-focus-visible:ring-violet-700">
              <input
                type="radio"
                name={`input-request-${request.id}`}
                checked={isOtherActive}
                onChange={() => {
                  setSelectedOptionId(null);
                  setIsOtherSelected(true);
                  detailsInputRef.current?.focus();
                }}
                className="sr-only"
              />
              <ShortcutBadge isSelected={isOtherActive}>
                {shortcutForIndex(request.options.length)}
              </ShortcutBadge>
              <span
                className={cn(
                  "pt-0.5 leading-5 text-gray-400",
                  isOtherActive && "text-gray-700",
                )}
              >
                Other…
              </span>
            </label>
          )}
        </div>
        <div className="mt-4 flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onPress={() => onSubmit({ kind: "skip" })}
            isDisabled={isDisabled}
          >
            Skip
            {!hasAnswer && (
              <span aria-hidden className="font-normal text-gray-400">
                Esc
              </span>
            )}
          </Button>
          <Button
            variant="default"
            size="sm"
            isDisabled={!canSubmit}
            onPress={submit}
          >
            Continue
          </Button>
        </div>
      </fieldset>
      {showDetails && (
        <div className="border-t border-gray-200 px-4 py-3">
          <TextareaAutosize
            ref={detailsInputRef}
            value={details}
            onChange={(event) => onDetailsChange?.(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                handleEscape();
              } else if (
                event.key === "Enter" &&
                !event.shiftKey &&
                canSubmit
              ) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Add more optional details"
            minRows={1}
            maxRows={6}
            disabled={isDisabled}
            className="block w-full resize-none bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      )}
    </div>
  );
};
